import type { StatusTone } from '@vnetwork/ui/data';
import type { DomainStatus } from '@/api/manticore';

// Domain lifecycle → StatusPill tone + label. Ported from the legacy
// DOMAIN_STATES mapping in External.SwiftFederation/ManageDomains/DomainList.
export const DOMAIN_STATUS_TONE: Record<string, StatusTone> = {
  active: 'success',
  deploying: 'info',
  suspended: 'danger',
  disabled: 'muted',
  pending: 'warning',
};

export const DOMAIN_STATUS_LABEL: Record<string, string> = {
  active: 'Enabled',
  deploying: 'Deploying',
  suspended: 'Suspended',
  disabled: 'Disabled',
  pending: 'Pending',
};

export function domainStatusTone(status: DomainStatus | undefined): StatusTone {
  return DOMAIN_STATUS_TONE[String(status ?? '').toLowerCase()] ?? 'muted';
}

export function domainStatusLabel(status: DomainStatus | undefined): string {
  const key = String(status ?? '').toLowerCase();
  return DOMAIN_STATUS_LABEL[key] ?? (status ? String(status) : 'Unknown');
}

/**
 * Resolve a status tone to a REAL @vnetwork/ui v2 color token. Use for
 * `Stat dotColor`, icon `color`, etc. — never hand-write `--vn-tone-*` (those
 * don't exist in the design system and silently fall back to hard-coded hex
 * that breaks dark mode).
 *
 * @param variant 'solid' for swatches/icons (default), 'text' for readable text.
 */
export function toneColor(tone: StatusTone, variant: 'solid' | 'text' = 'solid'): string {
  switch (tone) {
    case 'success':
      return `var(--vn-success-${variant})`;
    case 'info':
      return `var(--vn-info-${variant})`;
    case 'warning':
      return `var(--vn-warning-${variant})`;
    case 'danger':
      return `var(--vn-danger-${variant})`;
    default:
      return variant === 'text' ? 'var(--vn-fg-muted)' : 'var(--vn-fg-subtle)';
  }
}

/** "Configured / Not configured" chip — shared across Cache, SSL, etc. */
export function configTone(isConfigured: boolean): StatusTone {
  return isConfigured ? 'success' : 'muted';
}
export function configLabel(isConfigured: boolean): string {
  return isConfigured ? 'Configured' : 'Not configured';
}

/** Domains that are mid-transition can't be deleted. */
export function isDomainDeletable(status: DomainStatus | undefined): boolean {
  return !['deploying', 'provisioning', 'issuing_cert'].includes(String(status ?? ''));
}

/** Strict UUID — FE only ever sends UUIDs to the BE (dots in FQDNs broke SPA routing). */
export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Date formatters are centralized in @/lib/format-date (which also rejects
// backend zero-dates). Re-exported so existing './shared' import sites work.
export { formatDate, formatDateTime } from '@/lib/format-date';

/** Small debounce hook (legacy used @/hooks/useDebounce, not in this repo). */
import { useEffect, useState } from 'react';
export function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
